import { Component, ViewChild, AfterViewInit, OnInit, Input } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { take } from 'rxjs/operators';
import { ApiService } from 'src/app/services/api.service';
import { AuthService } from 'src/app/services/auth.service';
import { DataService } from 'src/app/services/data.service';
import { SharedDataService } from '../../../shared/shared-data-service';
export interface FeedRow {
  indexName: string;
  description: string;
  useCase: string[];
  qualifiedOperator: string[];
}

@Component({
  selector: 'app-indices-preview',
  templateUrl: './indices-preview.component.html',
  styleUrls: ['./indices-preview.component.scss']
})
export class IndicesPreviewComponent implements OnInit, AfterViewInit {
  displayedColumns: string[] = [
    'indexName',
    'description',
    'useCase',
    'qualifiedOperator'
  ];
  
  dataSource = new MatTableDataSource<FeedRow>([]);

  private _sort!: MatSort;
  @ViewChild(MatSort)
  set sort(sort: MatSort) {
    if (!sort) {
      return;
    }
    this._sort = sort;
    this.applySort();
  }

  get sort(): MatSort {
    return this._sort;
  }
  currentUser: any;
  industryTypes: any;
  isAllIndustriesChecked: boolean = false;
  tooltipImagePath: string;
  selectedIndustryList: any[] = [];
  indicesData: any[] = [];

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private router: Router,
    private localStorageService: DataService,
    private sharedData: SharedDataService
  ) {
    this.localStorageService.darkTheme.subscribe((newValue) => {
      this.tooltipImagePath = newValue
        ? '../../../assets/images/union-dark.svg'
        : '../../../assets/images/union.svg';
    });
  }

  ngOnInit(): void {
    this.authService.user.pipe(take(1)).subscribe((user) => {
      this.currentUser = user;
      // Load industry types and indices data
      this.loadIndicesData();
      this.loadIndustryTypes();
    });
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;

    this.dataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'indexName':
          return item.indexName;
        case 'description':
          return item.description;
        case 'useCase':
          return item.useCase ? item.useCase.join(', ') : '';
        case 'qualifiedOperator':
          return item.qualifiedOperator ? item.qualifiedOperator.join(', ') : '';
        default:
          return '';
      }
    };
  }

  private loadIndicesData(): void { 
    this.apiService
      .getAllInformationServices(this.currentUser?.id)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
            this.indicesData = response?.vadpServices || [];
            if (this.selectedIndustryList.length > 0) {
              this.loadFilteredData();
            }
        },
        error: (err) => {},
      });
  }

  // Load industry types from ApiService
  private loadIndustryTypes(): void {
    this.apiService.getIndustryTypes().pipe(take(1)).subscribe({
      next: (response) => {
        
        this.industryTypes = response.industriesList
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(item => ({
          ...item,
          selected: false
        }));

        this.syncSelectedIndustryList();
        this.syncAllIndustriesCheckbox();

        if (this.selectedIndustryList.length > 0 && this.indicesData.length > 0) {
          this.loadFilteredData();
        }
        
      },
      error: (err) => {},
    });
  }

  industryTypeChange(industry: any, index: number) {
    this.resetSorting();
    this.syncSelectedIndustryList();
    this.syncAllIndustriesCheckbox();
    this.selectedIndustryList.length === 0 ? this.dataSource.data = [] : this.loadFilteredData();
  };

  onAllIndustryTypesChange(checked: any): void {
    this.resetSorting();
    const isChecked = !!checked;
    (this.industryTypes || []).forEach((industry: any) => {
      industry.selected = isChecked;
    });

    this.syncSelectedIndustryList();
    this.syncAllIndustriesCheckbox();

    this.selectedIndustryList.length === 0 ? this.dataSource.data = [] : this.loadFilteredData();
  }

  private syncSelectedIndustryList(): void {
    this.selectedIndustryList = (this.industryTypes || [])
      .filter((industry: any) => !!industry.selected)
      .map((industry: any) => industry.name);
  }

  private syncAllIndustriesCheckbox(): void {
    const allIndustries = this.industryTypes || [];
    this.isAllIndustriesChecked =
      allIndustries.length > 0 &&
      allIndustries.every((industry: any) => !!industry.selected);
  }


  loadFilteredData(): void {
    const filteredData = this.indicesData.filter((feed: any) => {

    // Industry filter
    const industryMatch =
      Array.isArray(feed.industries) &&
      feed.industries.some((industry: any) =>
        this.selectedIndustryList.includes(industry.name)
      );

    // Sensor suspension check
    const hasActiveSensor =
      Array.isArray(feed.sensors) &&
      feed.sensors.some((sensor: any) => sensor?.suspended === false);

      return industryMatch && hasActiveSensor;
    });

    this.dataSource.data = filteredData?.length
      ? this.mapToTableRows(filteredData)
      : [];

    this.applySort();
  }

  private mapToTableRows(data: any[]): FeedRow[] {
    return data.map((item) => {

      const operators: string[] = item.sensors
        .filter((sensor: any) => !sensor?.suspended)
        .map((sensor: any) => {
          const name: string = this.currentUser.obfuscated
            ? sensor.obfuscateName
            : sensor.sensor;

          return `${name} ${sensor.sensorType}`;
        });

      const uniqueOperators: string[] = [...new Set(operators)].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

      return {
        indexName: item.name,
        description: item.description,
        useCase: item.useCases || [],
        qualifiedOperator: uniqueOperators
      };
    });
  }

  private applySort(): void {
    if (this.sort) {
      this.dataSource.sort = this.sort;
    }
  }

  private resetSorting(): void {
    if (this.sort) {
      this.sort.sort({ id: '', start: 'asc', disableClear: false });
      this.dataSource.sort = this.sort;
    }
  }

  
}
